import {
    AvroDataset,
    AzureSqlDatabaseLinkedService,
    AzureSqlDWLinkedService,
    Constants,
    compressionCodecType,
    DelimitedTextDataset,
    Encoding,
    FormatDatasetV2,
    IViolation,
    JsonDataset,
    LinkedService,
    ModelConstants,
    ParquetDataset,
    SelfHostedIntegrationRuntime,
    cosmosSqlAuthMethod,
    format,
    snowflakeAuthTypes,
    azureStorageAuthTypes,
    IValidationContext,
    IResourceContext,
    LakehouseLinkedService,
    lakehouseAuthTypes,
    DataWarehouseLinkedService,
    SnowflakeLinkedService,
    warehouseAuthTypes,
    servicePrincipalCredentialTypes,
    SqlServerLinkedService,
    SqlServerAuthType
} from 'shell-models-lib';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { formatListWithConjunction, sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { TabularTypeDataset } from 'app/model/dataset/tabular-type.dataset';
import { Metamodel } from 'app/model/base/model';

import { Dataset } from 'app/model/dataset/dataset';
import { AzureBlobDataset } from '../dataset/azure-blob.dataset';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { AzureDataLakeStoreLinkedService } from 'app/model/linkedservice/azure-data-lake-store.linkedservice';
import { ISupportsQuery } from './ISupportsQuery';
import { validateCompressionForDelimitedText } from 'app/shared/utils/activity-validation.util';
import { AzureBlobStorageLocation } from '../dataset/location/azure-blob.location';
import { DataFlowSource } from './dataflow-source';
import { DataFlowSink } from './dataflow-sink';
import { StorageAccountKind } from 'app/apis/azure-storage-constants';
import { XmlDataset } from '../dataset/xml.dataset';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { DataFlowExpression } from './dataflowexpression';
import { OrcDataset } from '../dataset/orc.dataset';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { AzureCosmosDBLinkedService } from '../linkedservice/azure-cosmos-db.linkedservice';
import { blobPathRegex } from 'app/shared/utils/connector-metadata-static-consts';
import { AzureBlobFSLocation } from '../dataset/location/azure-blobfs.location';
import { LoggerService } from 'app/logger/logger.service';
import { GlobalVariables } from 'app/services/globals';
import { findDataFlowSource, isSourceTypeEq } from 'app/model/dataflow/dataflow-utils';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

type DataFlowTarget = DataFlowSource | DataFlowSink;

const isTridentMdf: boolean = __IS_FABRIC_MDF__;

export async function transformationDatasetValidator(owner: DataFlowTarget, value: Dataset, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
    if ((owner as DataFlowSource).skipDatasetValidation) {
        return undefined;
    }

    // MDF: Check if sink type is selected (either Inline or Cache) - only for sinks
    if (isTridentMdf) {
        if (owner instanceof DataFlowSink) {
            if (!owner.isInlineDataset && !owner.isCacheSink) {
                return [{
                    message: () => format(stringResources.RequiredTemplate, stringResources.SinkType),
                    context: owner
                }];
            }
        }
        // MDF uses inline mode - skip dataset validation (sourceSinkLinkedServiceValidator handles connection validation)
        return undefined;
    }

    if (!owner.isWorkspaceDatabase && !owner.isInlineDataset && !(owner as DataFlowSink).isCacheSink && !value) {
        return [{
            message: () => format(stringResources.RequiredTemplate, stringResources.ModelName_DataSet),
            context: owner
        }];
    }

    if (!value || !value.linkedService) {
        return undefined;
    }
    const violations = (await validateLinkedService(owner, value.linkedService, violation, context)).concat((await validateDataset(owner, value, context.contextData)));

    return violations.length > 0 ? violations : undefined;
}

export async function sourceSinkLinkedServiceValidator(owner: DataFlowTarget, value?: LinkedService, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
    if (!owner.isInlineDataset || Metamodel.modelOf(owner.owner).name === 'call') {
        return undefined;
    }

    if (!value) {
        // MDF: Show "Connection is required" instead of generic "Linked service is required"
        const label = isTridentMdf ? stringResources.ConnectionLabel : stringResources.LinkedService;
        // MDF: Navigate to tab 0 instead of opening linked service panel (not applicable for MDF)
        // ADF: No redirectTo - default behavior opens linked service panel
        const violation: IViolation = {
            message: () => format(stringResources.RequiredTemplate, label),
            context: owner
        };
        if (isTridentMdf) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            violation.redirectTo = { entity: owner, attribute: { name: 'mdfConnection' } as any };
        }
        return [violation];
    }

    const violations = await validateLinkedService(owner, value, violation, context);

    return violations.length > 0 ? violations : undefined;
}

/**
 * MDF: Validates that connection type is selected for MDF Lakehouse/Warehouse sources and sinks.
 */
export function mdfConnectionTypeValidator(owner: DataFlowTarget, value?: DatasetFormatType): IViolation[] | undefined {
    if (!isTridentMdf) {
        return undefined;
    }

    // Only validate for inline datasets - cache sinks and non-inline modes don't use mdfConnectionType
    if (!owner?.isInlineDataset) {
        return undefined;
    }

    // If mdfConnectionType is already set, validation passes
    if (value) {
        return undefined;
    }

    // For saved MDFs, mdfConnectionType may not be set yet, but we can derive it from linked service
    // If a Lakehouse or Warehouse linked service is present, connection type is implicitly selected
    const linkedService = (owner as DataFlowSource).sourceLinkedService || (owner as DataFlowSink).sinkLinkedService;
    if (linkedService instanceof LakehouseLinkedService || linkedService instanceof DataWarehouseLinkedService) {
        return undefined; // Connection type can be inferred from linked service
    }

    // For regular (non-Lakehouse/Warehouse) connectors, the selected connection type is persisted as the
    // inline dataset format (datasetSettings), which is restored on reopen regardless of whether a
    // connection was chosen. A present format therefore means the connection type IS selected. Derive it
    // here so validation is robust when the transient mdfConnectionType hasn't been set by the panel yet
    // (e.g. reopen + Validate without opening/editing the source or sink panel).
    const datasetSettings: DataFlowDatasetSettings | undefined = owner.datasetSettings;
    if (datasetSettings?.getFormat() != null) {
        return undefined; // Connection type is selected (inline dataset format is set)
    }

    // No connection type and no Lakehouse/Warehouse linked service - show error
    return [{
        message: () => format(stringResources.RequiredTemplate, stringResources.Oracle_ResourceTypeLabel),
        context: owner,
        // Navigate to tab 0 (Source/Sink Settings) - do not open linked service panel for MDF
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        redirectTo: { entity: owner, attribute: { name: 'mdfConnectionType' } as any }
    }];
}

/**
 * MDF: Validates that format is selected when Lakehouse Files connection type is used.
 * This is separate from mdfConnectionTypeValidator so that "Connection type" and "Format"
 * violations can be independently tracked as fixed.
 *
 * Note: This validator is attached to `datasetSettings` attribute, so `value` is the
 * DataFlowDatasetSettings object. We check `datasetSettings.getFormat()` to see if a
 * format has been selected.
 */
export function mdfFormatValidator(owner: unknown, value?: unknown, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
    if (!isTridentMdf) {
        return undefined;
    }

    const target = owner as DataFlowTarget;
    // Only validate for inline datasets - cache sinks and non-inline modes don't use mdfConnectionType
    if (!target?.isInlineDataset) {
        return undefined;
    }
    // Only validate format when Lakehouse Files is selected
    // value is datasetSettings - check if format is set via getFormat()
    const datasetSettings = value as DataFlowDatasetSettings | undefined;
    const hasFormat = datasetSettings?.getFormat() != null;
    if (target?.mdfConnectionType === DatasetFormatType.LakehouseFiles && !hasFormat) {
        return [{
            message: () => format(stringResources.RequiredTemplate, stringResources.Format_Label),
            context: target,
            // Navigate to tab 0 (Source/Sink Settings) where format dropdown is located
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            redirectTo: { entity: target, attribute: { name: 'mdfFormat' } as any }
        }];
    }

    return undefined;
}

export async function validateLinkedService(transformation: DataFlowTarget, linkedService: LinkedService, violation?: IViolation, context?: IValidationContext): Promise<IViolation[]> {
    const violations: IViolation[] = [];
    const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(context.contextData, 'LinkedServiceRegistryService');

    const lsModel = Metamodel.modelOf(linkedService);
    if (linkedService.integrationRuntime instanceof SelfHostedIntegrationRuntime &&
        !linkedServiceRegistryService.getLinkedServiceConfiguration(Metamodel.modelOf(linkedService).name).isSHIRAllowedInDataFlow) {
        if (lsModel.name === adfModelTypeMap.sqlServer.linkedServiceType) {
            return [{
                message: () => stringResources.Dataflow_SqlServer_IRError
            }];
        }
        return [{
            message: () => stringResources.Dataflow_LinkedService_IRError
        }];
    }

    if (lsModel.name === adfModelTypeMap.sqlServer.linkedServiceType && (linkedService as SqlServerLinkedService).authenticationType === SqlServerAuthType.WindowsAuthentication) {
        return [{
            message: () => stringResources.Dataflow_SqlServer_AuthTypeError
        }];
    }

    if (
        Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSink
        && LinkedServiceUtils.isLinkedServiceSupportAlwaysEncrypted(linkedService)
    ) {
        violations.push({
            message: () => format(stringResources.Dataflow_Sink_LinkedService_SQLEncryption_NotSupported, lsModel.name)
        });
    }

    /**
     * Microsoft 365 dataflow source only supports ADLS Gen2, SQL DW/SQL Server and Azure Blob storage as sink.
     * Blob storage and ADLS Gen2 only allow Service Principal Access.
     * SQL DW and SQL Server allow Service Principal and UAMI.
     */
    if (transformation instanceof DataFlowSink) {
        const source = findDataFlowSource(transformation);
        if (isSourceTypeEq(adfModelTypeMap.office365.linkedServiceType, adfModelTypeMap.office365.datasetType)(source)) {
            const attribute = transformation.isInlineDataset
                ? DataFlowSink.MODEL.attribute('sinkLinkedService')
                : DataFlowSink.MODEL.attribute('sinkDataset');
            const linkedServiceType = Metamodel.modelOf(linkedService).name;
            const linkedServiceDisplayName = linkedServiceRegistryService.getLinkedServiceConfiguration(linkedServiceType).displayName;
            if ([ModelConstants.adlsGen2LinkedService, ModelConstants.blobLinkedService].includes(linkedServiceType)) {
                if (!LinkedServiceUtils.isServicePrincipalAuthType(linkedService)) {
                    violations.push({
                        message: () => sanitizeAndFormatString(
                            stringResources.Microsoft365MDFAuthTypeError,
                            linkedServiceDisplayName,
                            stringResources.AuthenticationType_ServicePrincipal
                        ),
                        redirectTo: {
                            entity: transformation,
                            attribute
                        }
                    });
                }
            } else if ([ModelConstants.azureSqlDatabase, ModelConstants.synapseLinkedService].includes(linkedServiceType)) {
                if (
                    !LinkedServiceUtils.isServicePrincipalAuthType(linkedService)
                    && !LinkedServiceUtils.isUAMIAuthType(linkedService)
                ) {
                    violations.push({
                        message: () => sanitizeAndFormatString(
                            stringResources.Microsoft365MDFAuthTypeError,
                            linkedServiceDisplayName,
                            formatListWithConjunction([stringResources.AuthenticationType_ServicePrincipal, stringResources.AuthenticationType_UserAssignedManagedIdentity], stringResources.And)
                        ),
                        redirectTo: {
                            entity: transformation,
                            attribute
                        }
                    });
                }
            } else {
                violations.push({
                    message: () => sanitizeAndFormatString(
                        stringResources.Microsoft365MDFAllowSink,
                        formatListWithConjunction([stringResources.AzureBlobStorage_DisplayText, stringResources.AzureBlobFS_DisplayText, stringResources.AzureSql_DisplayText, stringResources.AzureSynapseAnalytics], stringResources.And)
                    ),
                    redirectTo: {
                        entity: transformation,
                        attribute
                    }
                });
            }
        }
    }

    if(!isTridentMdf && (transformation instanceof DataFlowSource || transformation instanceof DataFlowSink) && isFabricDWAuthenticationTypeNotSPNKey(linkedService)) {
        violations.push({
            message: () => stringResources.Dataflow_FabricDW_AuthError,
            context: transformation
        });
    }

    if (linkedService instanceof AzureBlobStorageLinkedService) {
        if (linkedService.authenticationMethod !== azureStorageAuthTypes.AccountKeyAuthentication) {
            if (linkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication) {
                // enable sas auth
            } else if (
                linkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication
                || linkedService.authenticationMethod === azureStorageAuthTypes.ServicePrincipalAuthentication
                || (linkedService.authenticationMethod === azureStorageAuthTypes.UserAssignedManagedIdentity)
                || isCredentialAuth(linkedService)
            ) {
                if (
                    linkedService.accountKind !== StorageAccountKind.GeneralPurposeV2
                    && linkedService.accountKind !== StorageAccountKind.BlobStorage
                    && linkedService.accountKind !== StorageAccountKind.BlockBlobStorage
                ) {
                    violations.push({
                        message: () => stringResources.Dataflow_LinkedService_SPN_MI_Cred_AccountKindError
                    });
                }
            } else if (linkedService.authenticationMethod === azureStorageAuthTypes.Anonymous) {
                violations.push({
                    message: () => format(stringResources.Anonymous_BlobStorage_ErrorTemplate, stringResources.DataflowLowerCase)
                });
            } else {
                violations.push({
                    message: () => format(stringResources.Dataflow_LinkedService_New_AuthTypeError, lsModel.name)
                });
            }
        }
    } else if (linkedService instanceof AzureDataLakeStoreLinkedService) {
        if (linkedService.authenticationType === AuthenticationType.ManagedServiceIdentity && !GlobalVariables.enableGen1MIAuthForMDF) {
            violations.push({
                message: () => format(stringResources.Dataflow_LinkedService_MSIAuthTypeError, lsModel.name)
            });
        }
    } else if (linkedService instanceof AzureCosmosDBLinkedService 
            && (linkedService.authenticationMethod !== cosmosSqlAuthMethod.connectionString 
            && linkedService.authenticationMethod !== cosmosSqlAuthMethod.userAssigedManagedIdentity
            && linkedService.authenticationMethod !== cosmosSqlAuthMethod.managedIdentity)) {
        violations.push({
            message: () => stringResources.Dataflow_Cosmos_Auth_Error
        });
    } else if (linkedService instanceof LakehouseLinkedService) {
        if (linkedService.authenticationMethod !== lakehouseAuthTypes.ServicePrincipalAuthentication
        && linkedService.authenticationMethod !== lakehouseAuthTypes.UserAssignedManagedIdentity
        && linkedService.authenticationMethod !== lakehouseAuthTypes.MSIAuthentication
        && linkedService.authenticationMethod !== lakehouseAuthTypes.UserAuth) {
            violations.push({
                message: () => stringResources.Dataflow_LinkedService_AuthMethodError
            });
        }
    } else {
        const lsConfig = linkedServiceRegistryService.getLinkedServiceConfiguration(lsModel.name);
        const supportDataFlowSource = Boolean(
            typeof lsConfig.supportDataFlowSource === 'function'
                ? lsConfig.supportDataFlowSource()
                : lsConfig.supportDataFlowSource
        );
        const supportDataFlowSink = Boolean(
            typeof lsConfig.supportDataFlowSink === 'function'
                ? lsConfig.supportDataFlowSink()
                : lsConfig.supportDataFlowSink
        );
        if (!supportDataFlowSource && !supportDataFlowSink) {
            // do not support dataflow
            violations.push({
                message: () => format(stringResources.Dataflow_LinkedService_NotSupported, lsModel.name)
            });
        } else {
            if (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSource && !supportDataFlowSource) {
                // do not support source, only support sink
                violations.push({
                    message: () => format(stringResources.Dataflow_LinkedService_Source_NotSupported, lsModel.name)
                });
            }
            if (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSink && !supportDataFlowSink) {
                // do not support sink, only support source, such as AmazonS3
                violations.push({
                    message: () => format(stringResources.Dataflow_LinkedService_Sink_NotSupported, lsModel.name)
                });
            }
        }
    }

    if (linkedService instanceof SnowflakeLinkedService &&
        linkedService.authenticationType === snowflakeAuthTypes.OAuth) {
        violations.push({
            message: () => stringResources.Dataflow_Snowflake_OAuthError
        });
    }

    return violations;
}

function isCredentialAuth(linkedService: AzureBlobStorageLinkedService): boolean {
    return linkedService.authenticationMethod === azureStorageAuthTypes.Credential;
}

async function validateDataset(transformation: DataFlowTarget, dataset: Dataset, contextData: IResourceContext): Promise<IViolation[]> {
    const violations: IViolation[] = [];

    if (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSource && dataset instanceof FormatDatasetV2 &&
        !(dataset instanceof AvroDataset || dataset instanceof ParquetDataset || dataset instanceof OrcDataset)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hasWildcard = !arrayIsNullOrEmpty((transformation as any).wildcardPaths.filter((path: DataFlowExpression) => path.expression));
        const compressionCodecAttr = Metamodel.modelOf(dataset).hasAttribute('compressionCodec') ? Metamodel.modelOf(dataset).attribute('compressionCodec') : undefined;
        if (!hasWildcard && compressionCodecAttr && compressionCodecAttr.value(dataset) === compressionCodecType.gzip &&
            dataset.location && !dataset.location.isFolder() && dataset.location.getLocation().fileName != null && !dataset.location.getLocation().fileName.endsWith('.gz')) {
            violations.push({
                message: () => stringResources.Dataset_FileFormat_GZipFilenameError,
                redirectTo: {
                    entity: dataset,
                    attribute: Metamodel.modelOf(dataset.location).hasAttribute('fileName') ? Metamodel.modelOf(dataset.location).attribute('fileName') : compressionCodecAttr
                }
            });
        }
    }
    if (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSource && dataset instanceof FormatDatasetV2 && GlobalVariables.containerValidation) {
        if (dataset.location instanceof AzureBlobStorageLocation || dataset.location instanceof AzureBlobFSLocation) {
            const isBlob = dataset.location instanceof AzureBlobStorageLocation;
            const containerType = isBlob ? stringResources.ModelName_Container : stringResources.FileSystem_DisplayText;
            const attribute = isBlob ? AzureBlobStorageLocation.CONTAINER : AzureBlobFSLocation.CONTAINER;
            if (isBlob ? !AzureBlobStorageLocation.CONTAINER.templateValue(dataset.location) : !AzureBlobFSLocation.CONTAINER.templateValue(dataset.location)) {
                if (stringIsNullOrEmpty(dataset.location.container)) {
                    violations.push({
                        message: () => format(stringResources.Dataflow_DatasetRootRequired, containerType),
                        redirectTo: {
                            attribute,
                            entity: dataset
                        }
                    });
                } else if (!Constants.slashSpaceValidation.test(dataset.location.container)) {
                    const errorMsg = format(stringResources.Dataset_FileName_SpaceSlashValidation, containerType);
                    (await getServiceInstance<LoggerService>(contextData, 'LoggerService')).error('DF dataset source validateDataset', errorMsg);

                    violations.push({
                        message: () => errorMsg,
                        redirectTo: {
                            attribute,
                            entity: dataset
                        }
                    });
                }
            }
        }
    }
    if (dataset instanceof AzureBlobDataset) {
        const pathRegex = blobPathRegex;
        if (!pathRegex.pattern.test(dataset.folderPath)) {
            violations.push({
                message: () => pathRegex.message,
                redirectTo: {
                    entity: dataset,
                    attribute: AzureBlobDataset.FOLDER_PATH
                }
            });
        }
    } else if (dataset instanceof DelimitedTextDataset) {
        if (dataset.columnDelimiter === '') {
            if (dataset.encodingName !== undefined && dataset.encodingName !== 'UTF-8') {
                violations.push({
                    message: () => stringResources.Dataset_FileFormat_ColumnDelimiterShouldNotBeNoneError,
                    redirectTo: {
                        entity: dataset,
                        attribute: DelimitedTextDataset.ENCODING_NAME
                    }
                });
            }
        }
        if (dataset.encodingName === 'UTF-7') {
            violations.push({
                message: () => stringResources.Dataset_FileFormat_UTF7IsNotSupported,
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.ENCODING_NAME
                }
            });
        }
        if (dataset.encodingName === Encoding.UTF_8_WITHOUT_BOM) {
            violations.push({
                message: () => stringResources.Dataset_FileFormat_UTF8WithoutBOMIsNotSupported,
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.ENCODING_NAME
                }
            });
        }
        if (!isTridentMdf && dataset.linkedService instanceof AzureBlobStorageLinkedService && dataset.location instanceof AzureBlobStorageLocation &&
            (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSink || !GlobalVariables.containerValidation)) {
            if (!dataset.location.container && !AzureBlobStorageLocation.CONTAINER.templateValue(dataset.location)) {
                violations.push({
                    message: () => stringResources.Trigger_ContainerNameRequired,
                    redirectTo: {
                        entity: dataset,
                        attribute: AzureBlobStorageLocation.CONTAINER
                    }
                });
            }
        }
        violations.push(...(validateCompressionForDelimitedText(dataset, transformation, true) || []));
    } else if (dataset instanceof XmlDataset) {
        if (compressionCodecType.ZipDeflate === dataset.compressionCodec) {
            return [{
                message: () => format(stringResources.Dataset_FileFormat_DataflowCompressionNotSupport, dataset.compressionCodec),
                redirectTo: {
                    entity: dataset,
                    attribute: XmlDataset.COMPRESSION_CODEC
                }
            }];
        }
    } else if (dataset instanceof TabularTypeDataset) {
        const supportsQuery = transformation as unknown as ISupportsQuery;
        if (!(supportsQuery !== null && supportsQuery.useQuery)) {
            if (stringIsNullOrEmpty(dataset.tableName) && !dataset.isV2) {
                violations.push({
                    message: () => sanitizeAndFormatString(stringResources.Dataflow_Dataset_TableNameRequired, transformation.name),
                    redirectTo: {
                        entity: dataset,
                        attribute: TabularTypeDataset.TABLE_NAME
                    }
                });
            }
        }
    } else if (dataset instanceof JsonDataset) {
        if (dataset.encodingName === Encoding.UTF_8_WITHOUT_BOM) {
            violations.push({
                message: () => stringResources.Dataset_FileFormat_UTF8WithoutBOMIsNotSupported,
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.ENCODING_NAME
                }
            });
        }
    }

    // Make sure dataset is V2 for Data Flow
    if (dataset.linkedService instanceof AzureSqlDWLinkedService || dataset.linkedService instanceof AzureSqlDatabaseLinkedService) {
        if (!dataset.isV2) {
            violations.push({
                message: () => sanitizeAndFormatString(stringResources.DataFlowDatasetUpgradeError, dataset.name),
                redirectTo: {
                    entity: transformation
                }
            });
        }
    } else if (!dataset.isV2) {
        violations.push({
            message: () => sanitizeAndFormatString(stringResources.DataFlowDatasetReplaceWithV2Error, dataset.name),
            redirectTo: {
                entity: transformation
            }
        });
    }

    return violations;
}

const isFabricDWAuthenticationTypeNotSPNKey = (linkedService: LinkedService): boolean => {
    return (linkedService instanceof DataWarehouseLinkedService 
        && linkedService.authenticationMethod === warehouseAuthTypes.ServicePrincipalAuthentication 
        && linkedService.servicePrincipalCredentialType != servicePrincipalCredentialTypes.ServicePrincipalKey)
}
